import { MarketPlaceFilterDialog } from 'components/marketplace-filter-dialog/marketplace-filter-dialog';
import { Button, Card, Flex, MultiSelect, Select, Toggle } from 'components/ui';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { useMarketplaceConfig } from 'hooks/use-marketplace-config';
import { useUrlFilters } from 'hooks/use-url-filters';
import {
  ListingVisibilityFilterEnum,
  addAllItem,
  calculateNetworkFilterValues,
  getHeadNetworks,
  getListOfNetworks,
  getMyNetworks,
  listingStatusItems,
  listingVisibilityFilterItems,
  productType,
  states,
  stockCategory,
  types,
} from 'lib/marketplace-utils';
import { useStore } from 'lib/store';
import { useRouter } from 'next/router';
import { FC, useMemo } from 'react';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';
import { MarketplaceFilterCardEnum } from '../marketplace/filters/helpers';

const FilterCard = styled(Card)`
  display: flex;
  flex-direction: column;

  gap: 6px;
  padding: ${globalTheme.space[2]};
  overflow-wrap: wrap;
  background-color: ${globalTheme.colors.background};
  border-radius: 20px;
  @media screen and (max-width: ${breakpoints.small}) {
    background-color: transparent;
    padding: 0px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 10px;
  width: 150px;
`;

const FilterContent = styled.div`
  display: flex;
  gap: 6px;
  justify-content: space-between;
  overflow-wrap: wrap;
  flex-direction: column;
`;

const SoldFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 150px;
`;

const Label = styled.span`
  font-weight: 700;
  font-size: ${globalTheme.fontSizes[1]};
`;

const HeadingText = styled.span`
  font-weight: 700;
  font-size: ${globalTheme.fontSizes[3]};

  @media screen and (max-width: ${breakpoints.small}) {
    display: none;
  }
`;

const ButtonContainer = styled.div<{ wrap?: boolean }>`
  display: flex;
  flex-wrap: ${(props) => (props.wrap ? 'wrap' : 'nowrap')};
  gap: 6px;

  @media screen and (max-width: ${breakpoints.small}) {
    justify-content: flex-start;
  }
`;
const FilterButton = styled(Button)<{ isSmall?: boolean }>`
  height: 40px;
  @media screen and (max-width: ${breakpoints.small}) {
    width: 100%;
  }
`;

const FilterButtonMax = styled(FilterButton)<{ isSmall?: boolean }>`
  > div {
    margin-inline-end: unset;
    min-width: 20px;
  }
`;

const LeftContent = styled.div`
  display: flex;
  gap: 20px;
  @media screen and (max-width: ${breakpoints.large}) {
    flex-wrap: wrap;
  }
  @media screen and (max-width: ${breakpoints.small}) {
    display: none;
  }
`;

const PillWrapper = styled.div<{ showPills?: boolean }>`
  display: none;

  @media screen and (max-width: ${breakpoints.small}) {
    display: ${(props) => (props?.showPills ? 'flex' : 'none')};
    gap: 10px;
  }
`;

const RightContent = styled.div`
  display: flex;
  gap: 30px;
  margin-top: ${globalTheme.space[2]};
  @media screen and (max-width: ${breakpoints.medium}) {
    ${ButtonContainer} {
      display: flex;
      width: 100%;
    }
  }

  @media screen and (max-width: ${breakpoints.small}) {
    .status-filter {
      display: none;
    }
    margin-top: 0;
    flex-direction: column;
    gap: 20px;
  }
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: #ffffff;
  background-color: ${globalTheme.colors.blue};
  border-radius: 50%;
  min-width: 18px;
`;

const PillButton = styled(Button)<{ isActive: boolean }>`
  border: ${(props) => (props.isActive ? '2px solid #1a1a1a' : '')};
  flex: 1;

  &.private-btn {
    background-color: ${globalTheme.colors.red};

    color: ${globalTheme.colors.white};
  }

  &.public-btn {
    background-color: ${globalTheme.colors.darkBlue};

    color: ${globalTheme.colors.white};
  }

  &.sold-btn {
    background-color: ${globalTheme.colors.lightPink};

    color: ${globalTheme.colors.pink};
  }
`;

export enum PillParam {
  ALL = '',
  DRAFT = 'DRAFT',
  PUBLISHED = 'published',
  REVIEWING = 'reviewing',
  SOLD = 'sold',
  BUY = 'buy',
  SELL = 'sell',
  PRIVATE = 'private',
  LIVE = 'live',
  PUBLIC = 'public',
}

interface MarketplaceFilterCardProps {
  instance: MarketplaceFilterCardEnum;
  quickFilterButtons?: boolean;
  route?: string;
}

const MarketplaceFilterCard: FC<MarketplaceFilterCardProps> = ({
  instance,
  quickFilterButtons,
  route,
}) => {
  const [filterDialogOpen, openFilterDialog, closeFilterDialog] =
    useDialogToggle(false);
  const router = useRouter();
  const isMobile = useMedia(`(max-width: ${breakpoints.small})`, false);
  const activeOrgId = useStore((store) => store.activeBuyerOrgId);
  const {
    filters,
    getArrayFilter,
    getStringFilter,
    setFilter,
    clearAllFilters,
  } = useUrlFilters();

  const typeValue = getStringFilter('type', 'all');
  const productValue = getStringFilter('product', 'all');
  const statesValue = getArrayFilter('state');
  const soldValue = getStringFilter('sold', 'all');
  const auctionValue = getStringFilter('auction', 'all');
  const stockCategoryValue = getArrayFilter('stockCat');
  const networksValue = getArrayFilter('networks', ['all-listings']);
  const listingStatus = getArrayFilter('public_status');
  const listingVisibility = getArrayFilter('visibility');

  const activePill =
    getStringFilter('visibility', '') || getStringFilter('sold', '');

  const { data: marketplaceConfig, isLoading: isMarketplaceConfigLoading } =
    useMarketplaceConfig();

  const myNetworksIds = useMemo(() => {
    return [
      ...getMyNetworks(marketplaceConfig?.networks, activeOrgId as string),
      ...getHeadNetworks(marketplaceConfig?.networks),
    ].map((item) => item.id);
  }, [marketplaceConfig, activeOrgId]);

  const effectiveNetworksValue = useMemo(() => {
    return networksValue;
  }, [networksValue]);

  const NETWORKS_ACTIVE =
    !isMarketplaceConfigLoading &&
    marketplaceConfig &&
    !!marketplaceConfig.networks.length;

  const handleMoreFilter = () => {
    openFilterDialog();
  };

  const badgeCount = useMemo(() => {
    // `id`/`slug` are route params (the gone-listing view renders the
    // marketplace on /marketplace/listing/[id]), not filters.
    const ignorableKeys = ['page', 'pageSize', 'tab', 'id', 'slug'];
    return Object.keys(filters).filter(
      (key) => !ignorableKeys.includes(key) && filters[key]
    ).length;
  }, [filters]);

  const clearFilter = () => {
    clearAllFilters();
  };

  const productItems = addAllItem(
    productType.map((item) => ({ id: item.id, value: item.title }))
  );
  const statesItems = addAllItem(
    states.map((item) => ({ id: item, value: item }))
  );
  const typeItems = addAllItem(
    types.map((item) => ({
      id: item.value,
      value: item.label,
    }))
  );

  const networkItems = NETWORKS_ACTIVE && [
    { id: 'all-listings', value: 'All Listings' },
    {
      id: 'all',
      value: 'Networks only listings',
    },
    ...getListOfNetworks(marketplaceConfig).map((item) => ({
      id: item.id,
      value: item.name,
    })),
  ];

  const stockCategoryItems = addAllItem(
    stockCategory.map((item) => ({ id: item, value: item }))
  );

  const handleButtonClick = (id: string) => {
    clearAllFilters();
    switch (id) {
      case PillParam.PRIVATE:
        setFilter('visibility', ListingVisibilityFilterEnum.Private);
        break;

      case PillParam.PUBLIC:
        setFilter('visibility', ListingVisibilityFilterEnum.Public);
        break;

      case PillParam.SOLD:
        setFilter('sold', 'true');
        break;
    }
  };

  return (
    <>
      <FilterCard>
        <HeadingText>Filters</HeadingText>
        <FilterContent>
          <LeftContent>
            {/** Trading type */}
            {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
              <FilterContainer>
                <Label>Wanted / For Sale</Label>
                <Select
                  data-cy={`TypeSelectInput`}
                  items={typeItems}
                  placeholder='Type'
                  initialSelectedOption={typeValue}
                  value={typeValue}
                  onChange={async (selected) => {
                    setFilter('type', selected?.id as string);
                  }}
                />
              </FilterContainer>
            )}

            {/** Status */}
            {instance !== MarketplaceFilterCardEnum.MARKETPLACE && (
              <FilterContainer>
                <Label>Public status</Label>
                <MultiSelect
                  data-cy={`MultiSelectInput`}
                  items={listingStatusItems}
                  placeholder='All'
                  initialSelectedOptions={listingStatus}
                  value={listingStatus}
                  onChange={async (selected) => {
                    const selectedValues = (
                      selected?.map((item) => item.id) || []
                    ).filter(
                      (id): id is string => id !== null && id !== undefined
                    );
                    setFilter('public_status', selectedValues);
                  }}
                />
              </FilterContainer>
            )}

            {/** Visibility*/}
            {instance !== MarketplaceFilterCardEnum.MARKETPLACE && (
              <FilterContainer>
                <Label>Visibility</Label>
                <MultiSelect
                  data-cy={`VisibilitySelectInput`}
                  items={listingVisibilityFilterItems}
                  placeholder='All'
                  initialSelectedOptions={listingVisibility}
                  value={listingVisibility}
                  onChange={async (selected) => {
                    const selectedValues = (
                      selected?.map((item) => item.id) || []
                    ).filter(
                      (id): id is string => id !== null && id !== undefined
                    );
                    setFilter('visibility', selectedValues);
                  }}
                />
              </FilterContainer>
            )}

            {/** Stock type*/}
            <FilterContainer>
              <Label>Stock type </Label>
              <Select
                data-cy={`StockSelectInput`}
                items={productItems}
                placeholder='Stock type'
                initialSelectedOption={productValue}
                value={productValue ? productValue : 'all'}
                onChange={async (selected) => {
                  setFilter('product', selected?.id as string);
                }}
              />
            </FilterContainer>

            {/**States*/}
            {statesValue && instance === MarketplaceFilterCardEnum.MARKETPLACE && (
              <FilterContainer>
                <Label>State</Label>
                <MultiSelect
                  data-cy={`MultiSelectInput`}
                  items={statesItems}
                  placeholder='State'
                  initialSelectedOptions={statesValue}
                  value={statesValue}
                  onChange={async (selected) => {
                    const selectedValues = (
                      selected?.map((item) => item.id) || []
                    ).filter(
                      (id): id is string => id !== null && id !== undefined
                    );
                    setFilter('state', selectedValues);
                  }}
                />
              </FilterContainer>
            )}

            {/** Stock category */}
            {instance === MarketplaceFilterCardEnum.MARKETPLACE && (
              <FilterContainer>
                <Label>Stock category</Label>
                <MultiSelect
                  data-cy={`MultiSelectInput`}
                  items={stockCategoryItems}
                  placeholder='Stock Category'
                  initialSelectedOptions={stockCategoryValue}
                  value={stockCategoryValue}
                  onChange={async (selected) => {
                    const selectedValues = (
                      selected?.map((item) => item.id) || []
                    ).filter(
                      (id): id is string => id !== null && id !== undefined
                    );
                    setFilter('stockCat', selectedValues);
                  }}
                />
              </FilterContainer>
            )}

            {/** Networks */}
            {NETWORKS_ACTIVE && networkItems && (
              <FilterContainer>
                <Label>Networks</Label>
                <MultiSelect
                  data-cy={`MultiSelectInput`}
                  items={networkItems}
                  placeholder='All listings'
                  initialSelectedOptions={networksValue}
                  value={effectiveNetworksValue}
                  onChange={async (selected) => {
                    const newSelectedIds = (
                      selected?.map((item) => item.id) || []
                    ).filter(
                      (id): id is string => id !== null && id !== undefined
                    );

                    const cleanIds = calculateNetworkFilterValues(
                      newSelectedIds,
                      effectiveNetworksValue,
                      myNetworksIds
                    );

                    setFilter('networks', cleanIds);
                  }}
                />
              </FilterContainer>
            )}
            {/** Filled Status*/}
            {soldValue && (
              <SoldFilterContainer style={{ width: 'max-content' }}>
                <Label>Hide filled/sold listings</Label>
                <Toggle
                  onChange={(e) => {
                    setFilter('sold', e.target.checked ? 'false' : '');
                  }}
                  checked={soldValue === 'false'}
                  showLabel={false}
                />
              </SoldFilterContainer>
            )}
            {/** Auction */}
            {auctionValue && (
              <SoldFilterContainer style={{ width: 'max-content' }}>
                <Label>Hide Auctions</Label>
                <Toggle
                  onChange={(e) => {
                    setFilter('auction', e.target.checked ? 'false' : '');
                  }}
                  checked={auctionValue === 'false'}
                  showLabel={false}
                />
              </SoldFilterContainer>
            )}
          </LeftContent>
          <RightContent>
            <PillWrapper showPills={quickFilterButtons}>
              <PillButton
                variant='secondary'
                size='small'
                key='private'
                className='btn private-btn'
                isActive={listingVisibility.includes(
                  ListingVisibilityFilterEnum.Private
                )}
                onClick={() => {
                  handleButtonClick(PillParam.PRIVATE);
                }}
              >
                Private
              </PillButton>
              <PillButton
                variant='secondary'
                size='small'
                key='public'
                className='btn public-btn'
                isActive={listingVisibility.includes(
                  ListingVisibilityFilterEnum.Public
                )}
                onClick={() => {
                  handleButtonClick(PillParam.PUBLIC);
                }}
              >
                Public
              </PillButton>
              <PillButton
                variant='secondary'
                size='small'
                key='sold'
                className='btn sold-btn'
                isActive={PillParam.SOLD === activePill}
                onClick={() => {
                  handleButtonClick(PillParam.SOLD);
                }}
              >
                Filled
              </PillButton>
            </PillWrapper>
            <Flex gap={2} direction='column'>
              <ButtonContainer>
                <FilterButtonMax
                  iconLeft='FilterIcon'
                  size='small'
                  variant='outline'
                  onClick={handleMoreFilter}
                >
                  Filters
                  {badgeCount !== 0 && <Badge>{badgeCount}</Badge>}
                </FilterButtonMax>
                {badgeCount !== 0 && !isMobile && (
                  <FilterButton
                    disabled={badgeCount === 0}
                    iconRight='CancelIcon'
                    size='small'
                    variant='primary'
                    onClick={clearFilter}
                  >
                    Clear filters
                  </FilterButton>
                )}
              </ButtonContainer>
            </Flex>
          </RightContent>

          <MarketPlaceFilterDialog
            isOpen={filterDialogOpen}
            onDismiss={closeFilterDialog}
            clearAllFilters={clearFilter}
            initialFilters={filters}
            onApplyFilters={(newFilters) => {
              router.push(
                {
                  pathname: route as string,
                  query: newFilters as Record<string, string>,
                },
                undefined,
                { shallow: true }
              );
            }}
            instance={instance}
          />
        </FilterContent>
      </FilterCard>
    </>
  );
};

export default MarketplaceFilterCard;
